<template>
  <div class="analytics-dashboard">
    <!-- Header responsive -->
    <div class="header-section mb-6">
      <div class="header-title">
        <h1 class="text-h4 text-md-h3">Dashboard de Análisis</h1>
        <div v-if="periodInfo" class="text-caption text-grey mt-1 period-info">
          <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
          <span class="d-none d-md-inline">Período actual: {{ formatPeriodLabel(periodInfo.current) }}</span>
          <span class="d-md-none">{{ formatPeriodLabel(periodInfo.current) }}</span>
          <span class="mx-2 d-none d-md-inline">•</span>
          <br class="d-md-none">
          <span class="d-none d-md-inline">Comparando con: {{ formatPeriodLabel(periodInfo.previous) }}</span>
          <span class="d-md-none">vs {{ formatPeriodLabel(periodInfo.previous) }}</span>
        </div>
      </div>
      <div class="header-actions">
        <v-menu
          ref="filterMenu"
          v-model="showFilterMenu"
          :close-on-content-click="false"
          location="bottom"
          offset="8"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              color="primary"
              v-bind="props"
              prepend-icon="mdi-filter"
              class="mb-2 mb-md-0"
              size="default"
            >
              <span class="d-none d-sm-inline">Filtros</span>
            </v-btn>
          </template>
          <v-card min-width="300" max-width="400">
            <v-card-title class="d-flex justify-space-between align-center">
              <span>Filtros de Análisis</span>
              <v-btn
                icon="mdi-close"
                variant="text"
                size="small"
                @click="closeFilterMenu"
              ></v-btn>
            </v-card-title>
            <v-card-text>
              <v-select
                v-model="filters.warehouse"
                :items="warehouses"
                label="Bodega"
                item-title="name"
                item-value="id"
                clearable
                variant="outlined"
                density="compact"
                :loading="refreshing"
                @update:model-value="refreshData"
              ></v-select>
              <v-select
                v-model="filters.timeRange"
                :items="timeRanges"
                label="Rango de tiempo"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                class="mt-3"
                :loading="refreshing"
                @update:model-value="refreshData"
              ></v-select>
              <div v-if="refreshing" class="text-center mt-3">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="20"
                ></v-progress-circular>
                <span class="text-caption ml-2">Aplicando filtros...</span>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                variant="text"
                @click="closeFilterMenu"
              >
                Aplicar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        <v-btn
          color="success"
          prepend-icon="mdi-microsoft-excel"
          @click="exportToExcel"
          :loading="exporting"
          size="default"
        >
          <span class="d-none d-sm-inline">Exportar</span>
        </v-btn>
      </div>
    </div>

    <v-row v-if="loading">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-col>
    </v-row>

    <template v-else>
      <!-- KPIs principales -->
      <v-row>
        <v-col v-for="(kpi, index) in kpis" :key="index" cols="12" sm="6" md="3">
          <v-card :loading="refreshing" class="kpi-card">
            <v-card-text>
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="text-overline">{{ kpi.name }}</div>
                <v-icon :color="getKPITrendColor(kpi.trend)">
                  {{ getKPITrendIcon(kpi.trend) }}
                </v-icon>
              </div>
              <div class="text-h4 mb-2">{{ formatKPI(kpi.value, kpi.type) }}</div>
              <div class="d-flex align-center" :class="getKPITrendColor(kpi.trend)">
                <span class="text-caption">vs. período anterior</span>
                <v-chip
                  :color="getKPITrendColor(kpi.trend)"
                  size="small"
                  class="ml-2"
                >
                  {{ kpi.change }}%
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Análisis ABC y DIN -->
      <v-row class="mt-6">
        <!-- ABC se basa en el valor del inventario: solo si el negocio maneja precios -->
        <v-col cols="12" :md="manejaPrecios ? 6 : 12" v-if="manejaPrecios">
          <v-card :loading="refreshing">
            <v-card-title class="d-flex justify-space-between align-center">
              <span>Análisis ABC</span>
              <v-tooltip text="Clasificación basada en el valor del inventario">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props">mdi-help-circle-outline</v-icon>
                </template>
              </v-tooltip>
            </v-card-title>
            <v-card-text>
              <div class="chart-container mb-4">
                <canvas ref="abcChartRef" height="200" style="max-height: 200px;"></canvas>
                <div v-if="!hasABCData" class="no-data-message">
                  <v-icon size="48" color="grey">mdi-chart-pie</v-icon>
                  <p class="text-grey mt-2">No hay datos ABC para mostrar</p>
                  <p class="text-caption text-grey">Asegúrate de que los productos tengan costos y demanda anual configurados</p>
                </div>
              </div>
              <v-list>
                <v-list-item v-for="(category, key) in safeAbcAnalysis" :key="key">
                  <template v-slot:prepend>
                    <v-avatar :color="getABCColor(key)" size="32">
                      {{ key }}
                    </v-avatar>
                  </template>
                  <v-list-item-title>
                    {{ category.count }} productos ({{ (category.percentage || 0).toFixed(1) }}%)
                  </v-list-item-title>
                  <template v-slot:append>
                    <strong>{{ formatCurrency(category.value || 0) }}</strong>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card :loading="refreshing">
            <v-card-title class="d-flex justify-space-between align-center">
              <span>Análisis DIN</span>
              <v-tooltip text="Clasificación basada en características físicas del inventario">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props">mdi-help-circle-outline</v-icon>
                </template>
              </v-tooltip>
            </v-card-title>
            <v-card-text>
              <div class="chart-container mb-4">
                <canvas ref="dinChartRef" height="200" style="max-height: 200px;"></canvas>
                <div v-if="!hasDINData" class="no-data-message">
                  <v-icon size="48" color="grey">mdi-chart-pie</v-icon>
                  <p class="text-grey mt-2">No hay datos DIN para mostrar</p>
                  <p class="text-caption text-grey">Asegúrate de que los productos tengan categorías DIN asignadas</p>
                </div>
              </div>
              <v-list>
                <v-list-item v-for="(category, key) in safeDinAnalysis" :key="key">
                  <template v-slot:prepend>
                    <v-avatar :color="getDINColor(key)" size="32">
                      {{ key }}
                    </v-avatar>
                  </template>
                  <v-list-item-title>
                    {{ category.count }} productos ({{ (category.percentage || 0).toFixed(1) }}%)
                  </v-list-item-title>
                  <template v-slot:append>
                    <span class="text-caption">
                      {{ getDINDescription(key) }}
                    </span>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Productos con bajo stock -->
      <v-row class="mt-6">
        <v-col cols="12">
          <v-card :loading="refreshing">
            <v-card-title class="low-stock-header">
              <span>Productos con Bajo Stock</span>
              <v-spacer class="d-none d-md-flex"></v-spacer>
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Buscar"
                single-line
                hide-details
                density="compact"
                class="search-field"
              ></v-text-field>
            </v-card-title>
            <ResponsiveDataTable
              :headers="lowStockHeaders"
              :items="safeLowStockProducts"
              :search="search"
              :items-per-page="10"
            >
              <template v-slot:item.deficit="{ item }">
                <v-chip :color="getDeficitColor(item?.deficit || 0)" size="small">
                  {{ item?.deficit || 0 }}
                </v-chip>
              </template>
              <template v-slot:item.abc_category="{ item }">
                <v-chip :color="getABCColor(item?.abc_category)" size="small">
                  {{ item?.abc_category || 'N/A' }}
                </v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  @click="createPurchaseOrder(item)"
                  :disabled="!item"
                  class="action-btn"
                >
                  <v-icon>mdi-cart-plus</v-icon>
                </v-btn>
              </template>
            </ResponsiveDataTable>
          </v-card>
        </v-col>
      </v-row>

      <!-- Análisis de Rotación -->
      <v-row class="mt-6">
        <v-col cols="12">
          <v-card :loading="refreshing">
            <v-card-title class="rotation-header">
              <span>Análisis de Rotación</span>
              <div class="rotation-controls">
                <v-btn
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-filter"
                  @click="showProductSelector = true"
                  size="small"
                  class="mb-2 mb-md-0"
                >
                  <span class="d-none d-sm-inline">Seleccionar Productos ({{ selectedProducts.length }})</span>
                  <span class="d-sm-none">({{ selectedProducts.length }})</span>
                </v-btn>
                <v-select
                  v-model="rotationPeriod"
                  :items="rotationPeriods"
                  label="Período"
                  hide-details
                  density="compact"
                  class="period-select"
                  @update:model-value="loadRotationAnalysis"
                ></v-select>
              </div>
            </v-card-title>
            <v-card-text>
              <div v-if="selectedProducts.length > 0" class="mb-4">
                <div class="text-subtitle-2 mb-2">Productos seleccionados:</div>
                <div class="d-flex flex-wrap gap-1">
                  <v-chip
                    v-for="product in selectedProducts.slice(0, 5)"
                    :key="product.id"
                    size="small"
                    closable
                    @click:close="removeProduct(product.id)"
                  >
                    {{ product.name }}
                  </v-chip>
                  <v-chip
                    v-if="selectedProducts.length > 5"
                    size="small"
                    color="grey"
                  >
                    +{{ selectedProducts.length - 5 }} más
                  </v-chip>
                </div>
              </div>
              <div class="chart-container">
                <canvas ref="rotationChartRef" height="300"></canvas>
                <div v-if="!filteredRotationData || filteredRotationData.length === 0" class="no-data-message">
                  <v-icon size="48" color="grey">mdi-chart-bar</v-icon>
                  <p class="text-grey mt-2">No hay datos de rotación para mostrar</p>
                  <p class="text-caption text-grey">
                    {{ selectedProducts.length === 0 ? 'Selecciona productos para ver su análisis de rotación' : 'Los datos aparecerán cuando haya movimientos de inventario registrados' }}
                  </p>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Predicción de Demanda Futura -->
      <v-row class="mt-6">
        <v-col cols="12">
          <v-card :loading="refreshing">
            <v-card-title class="prediction-header">
              <div class="prediction-title">
                <span>Predicción de Demanda Futura</span>
                <div class="text-caption text-grey mt-1 d-none d-md-block">
                  Basada en rotación histórica de productos seleccionados
                </div>
              </div>
              <div class="prediction-controls">
                <v-select
                  v-model="predictionMonths"
                  :items="predictionPeriods"
                  label="Meses a predecir"
                  hide-details
                  density="compact"
                  class="months-select mb-2 mb-md-0"
                  @update:model-value="generateDemandPrediction"
                ></v-select>
                <v-btn
                  color="primary"
                  variant="text"
                  @click="generateDemandPrediction"
                  :loading="calculatingPredictions"
                  size="small"
                >
                  <span class="d-none d-sm-inline">Recalcular</span>
                  <v-icon class="d-sm-none">mdi-refresh</v-icon>
                </v-btn>
              </div>
            </v-card-title>
            <v-card-text>
              <div class="chart-container">
                <canvas ref="demandChartRef" height="300"></canvas>
                <div v-if="!selectedProducts || selectedProducts.length === 0" class="no-data-message">
                  <v-icon size="48" color="grey">mdi-chart-line</v-icon>
                  <p class="text-grey mt-2">Selecciona productos para ver predicción de demanda</p>
                  <p class="text-caption text-grey">Las predicciones se basarán en el historial de rotación de los productos seleccionados</p>
                </div>
                <div v-else-if="!demandPredictionData || demandPredictionData.length === 0" class="no-data-message">
                  <v-icon size="48" color="grey">mdi-chart-line</v-icon>
                  <p class="text-grey mt-2">No hay suficientes datos históricos</p>
                  <p class="text-caption text-grey">Se necesita al menos 30 días de historial para generar predicciones</p>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Modal de Selección de Productos -->
    <v-dialog v-model="showProductSelector" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Seleccionar Productos para Análisis</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="showProductSelector = false"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <v-text-field
              v-model="productSearch"
              prepend-inner-icon="mdi-magnify"
              label="Buscar productos..."
              hide-details
              density="compact"
              clearable
            ></v-text-field>
          </div>
          
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-subtitle-2">{{ selectedProducts.length }} productos seleccionados</span>
              <div>
                <v-btn
                  size="small"
                  variant="text"
                  @click="selectTopRotationProducts"
                >
                  Top 10 Rotación
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  @click="clearSelectedProducts"
                >
                  Limpiar Todo
                </v-btn>
              </div>
            </div>
            <v-divider></v-divider>
          </div>

          <v-list max-height="400" class="overflow-y-auto">
            <v-list-item
              v-for="product in filteredProducts"
              :key="product.id"
              @click="toggleProduct(product)"
            >
              <template v-slot:prepend>
                <v-checkbox
                  :model-value="isProductSelected(product.id)"
                  @click.stop="toggleProduct(product)"
                  hide-details
                ></v-checkbox>
              </template>
              <v-list-item-title>{{ product.name }}</v-list-item-title>
              <v-list-item-subtitle>
                SKU: {{ product.sku }} | Categoría: {{ product.category || 'Sin categoría' }}
              </v-list-item-subtitle>
              <template v-slot:append>
                <div class="text-right">
                  <div class="text-caption">Rotación</div>
                  <v-chip
                    size="small"
                    :color="getRotationColor(product.rotation_index)"
                  >
                    {{ (product.rotation_index || 0).toFixed(2) }}
                  </v-chip>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showProductSelector = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="applyProductSelection"
          >
            Aplicar Selección ({{ selectedProducts.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de Orden de Compra -->
    <v-dialog v-model="purchaseDialog" max-width="600">
      <v-card>
        <v-card-title>Crear Orden de Compra</v-card-title>
        <v-card-text>
          <v-form ref="purchaseForm">
            <v-text-field
              v-model="purchaseOrder.quantity"
              label="Cantidad a ordenar"
              type="number"
              :rules="[v => !!v || 'La cantidad es requerida']"
            ></v-text-field>
            <v-select
              v-model="purchaseOrder.supplier"
              :items="suppliers"
              label="Proveedor"
              item-title="name"
              item-value="id"
              :rules="[v => !!v || 'El proveedor es requerido']"
            ></v-select>
            <v-textarea
              v-model="purchaseOrder.notes"
              label="Notas"
              rows="3"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="purchaseDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="submitPurchaseOrder"
            :loading="submittingPurchase"
          >
            Crear Orden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import * as XLSX from 'xlsx'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'

// Importar Chart.js usando ES6 modules
import Chart from 'chart.js/auto'

// Store
const store = useStore()

// Eje "maneja precios": condiciona los indicadores valorizados (ABC se basa en valor)
const manejaPrecios = computed(() => store.getters['edition/manejaPrecios'])

// Referencias
const abcChartRef = ref(null)
const dinChartRef = ref(null)
const rotationChartRef = ref(null)
const demandChartRef = ref(null)
const purchaseForm = ref(null)
const filterMenu = ref(null)

// Estado
const loading = ref(true)
const refreshing = ref(false)
const exporting = ref(false)
const calculatingPredictions = ref(false)
const submittingPurchase = ref(false)
const search = ref('')
const showFilterMenu = ref(false)
const charts = ref({
  abc: null,
  din: null,
  rotation: null,
  demand: null,
  demandUpdating: false
})
const purchaseDialog = ref(false)
const purchaseOrder = ref({
  product: null,
  quantity: null,
  supplier: null,
  notes: ''
})

// Filtros y configuración
const filters = ref({
  warehouse: null,
  timeRange: 'last_30_days'
})

const timeRanges = [
  { title: 'Últimos 30 días', value: 'last_30_days' },
  { title: 'Últimos 60 días', value: 'last_60_days' },
  { title: 'Últimos 90 días', value: 'last_90_days' },
  { title: 'Este mes', value: 'this_month' },
  { title: 'Este trimestre', value: 'this_quarter' },
  { title: 'Este año', value: 'this_year' }
]

const rotationPeriod = ref(30)
const rotationPeriods = [
  { title: 'Últimos 30 días', value: 30 },
  { title: 'Últimos 60 días', value: 60 },
  { title: 'Últimos 90 días', value: 90 }
]

// Datos
const kpis = ref([])
const abcAnalysis = ref({})
const dinAnalysis = ref({})
const lowStockProducts = ref([])
const rotationAnalysis = ref([])
const warehouses = ref([])
const suppliers = ref([])
const periodInfo = ref(null)

// Nuevas variables para selección de productos y predicción
const showProductSelector = ref(false)
const productSearch = ref('')
const selectedProducts = ref([])
const allProducts = ref([])
const demandPredictionData = ref([])
const predictionMonths = ref(3)
const predictionPeriods = [
  { title: '3 meses', value: 3 },
  { title: '6 meses', value: 6 },
  { title: '12 meses', value: 12 }
]

// Headers para la tabla
const lowStockHeaders = [
  { title: 'SKU', key: 'sku', sortable: true },
  { title: 'Nombre', key: 'name', sortable: true },
  { title: 'Stock Mínimo', key: 'min_stock', sortable: true },
  { title: 'Stock Actual', key: 'current_stock', sortable: true },
  { title: 'Déficit', key: 'deficit', sortable: true },
  { title: 'Categoría ABC', key: 'abc_category', sortable: true },
  { title: 'Punto de Reorden', key: 'reorder_point', sortable: true },
  { title: 'Acciones', key: 'actions', sortable: false }
]

// Computed properties for safe data access
const safeAbcAnalysis = computed(() => abcAnalysis.value || {})
const safeDinAnalysis = computed(() => dinAnalysis.value || {})
const safeLowStockProducts = computed(() => lowStockProducts.value || [])

// Computed properties to check if there's data to display
const hasABCData = computed(() => {
  const data = safeAbcAnalysis.value
  return Object.values(data).some(category => category.count > 0)
})

const hasDINData = computed(() => {
  const data = safeDinAnalysis.value
  return Object.values(data).some(category => category.count > 0)
})

// Nuevas computed properties
const filteredProducts = computed(() => {
  if (!allProducts.value) return []
  
  let filtered = allProducts.value
  
  if (productSearch.value) {
    const search = productSearch.value.toLowerCase()
    filtered = filtered.filter(product => 
      product.name.toLowerCase().includes(search) ||
      product.sku.toLowerCase().includes(search) ||
      (product.category && product.category.toLowerCase().includes(search))
    )
  }
  
  return filtered
})

const filteredRotationData = computed(() => {
  if (!rotationAnalysis.value || selectedProducts.value.length === 0) return []
  
  const selectedIds = selectedProducts.value.map(p => p.id)
  return rotationAnalysis.value
    .filter(item => selectedIds.includes(item.id))
    .slice(0, 10) // Máximo 10 productos
    .sort((a, b) => (b.rotation_index || 0) - (a.rotation_index || 0))
})

// Métodos de utilidad
const formatKPI = (value, type) => {
  switch (type) {
    case 'currency':
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    case 'percentage':
      return `${value.toFixed(1)}%`
    case 'number':
      return new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    default:
      return value
  }
}

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatPeriodLabel = (period) => {
  if (!period) return ''
  
  const startDate = new Date(period.start_date)
  const endDate = new Date(period.end_date)
  
  const formatDate = (date) => {
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }
  
  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

const getKPITrendColor = (trend) => {
  switch (trend) {
    case 'up':
      return 'success'
    case 'down':
      return 'error'
    case 'neutral':
      return 'grey'
    default:
      return 'grey'
  }
}

const getKPITrendIcon = (trend) => {
  switch (trend) {
    case 'up':
      return 'mdi-arrow-up'
    case 'down':
      return 'mdi-arrow-down'
    case 'neutral':
      return 'mdi-minus'
    default:
      return 'mdi-minus'
  }
}

const getABCColor = (category) => {
  switch (category) {
    case 'A':
      return 'success'
    case 'B':
      return 'warning'
    case 'C':
      return 'error'
    default:
      return 'grey'
  }
}

const getDINColor = (category) => {
  switch (category) {
    case 'D':
      return 'success'
    case 'I':
      return 'warning'
    case 'N':
      return 'error'
    default:
      return 'grey'
  }
}

const getDINDescription = (category) => {
  switch (category) {
    case 'D':
      return 'Dimensiones estándar'
    case 'I':
      return 'Dimensiones intermedias'
    case 'N':
      return 'Dimensiones no estándar'
    default:
      return 'Sin clasificar'
  }
}

const getDeficitColor = (deficit) => {
  if (deficit <= 5) return 'warning'
  if (deficit <= 10) return 'error'
  return 'grey'
}

// Función para cerrar el menú de filtros
const closeFilterMenu = () => {
  showFilterMenu.value = false
}

// Función de utilidad para verificar si un canvas es válido
const isCanvasValid = (canvasRef) => {
  try {
    return canvasRef && 
           canvasRef.getContext && 
           typeof canvasRef.getContext === 'function' &&
           canvasRef.parentNode && // Verificar que está en el DOM
           canvasRef.offsetParent !== null && // Verificar que es visible
           canvasRef.width > 0 && // Verificar que tiene dimensiones
           canvasRef.height > 0
  } catch (error) {
    console.warn('Error checking canvas validity:', error)
    return false
  }
}

// Función para destruir un gráfico de forma segura
const safeDestroyChart = (chartKey) => {
  const chart = charts.value[chartKey]
  if (!chart) return
  
  try {
    // Detener todas las animaciones inmediatamente
    if (typeof chart.stop === 'function') {
      chart.stop()
    }
    
    // Cancelar cualquier animación pendiente usando Chart.js registry
    if (window.Chart && window.Chart.registry) {
      const animator = window.Chart.registry.getController('animator')
      if (animator) {
        animator.stop()
      }
    }
    
    // Limpiar el contexto del canvas de forma más agresiva
    if (chart.ctx && chart.ctx.canvas) {
      const canvas = chart.ctx.canvas
      const ctx = canvas.getContext('2d')
      if (ctx) {
        // Guardar el estado actual
        ctx.save()
        // Limpiar completamente
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        // Restaurar el estado
        ctx.restore()
      }
    }
    
    // Destruir el gráfico
    chart.destroy()
    //console.log(`${chartKey} chart destroyed safely`)
  } catch (error) {
    console.warn(`Error destroying ${chartKey} chart:`, error)
  } finally {
    charts.value[chartKey] = null
  }
}

// Función para limpiar completamente Chart.js
const cleanupChartJS = () => {
  try {
    // Limpiar el registro global de Chart.js si existe
    if (window.Chart && window.Chart.instances) {
      Object.keys(window.Chart.instances).forEach(key => {
        const instance = window.Chart.instances[key]
        if (instance && typeof instance.destroy === 'function') {
          try {
            instance.stop()
            instance.destroy()
          } catch (error) {
            console.warn('Error destroying Chart.js instance:', error)
          }
        }
      })
    }
    
    // Forzar garbage collection si está disponible
    if (window.gc) {
      window.gc()
    }
  } catch (error) {
    console.warn('Error during Chart.js cleanup:', error)
  }
}

// Función para verificar si el gráfico de demanda necesita actualizarse
const shouldUpdateDemandChart = () => {
  if (!charts.value.demand) {
    //console.log('Demand chart does not exist, needs creation')
    return true
  }
  
  const currentPeriod = rotationPeriod.value || 30
  const expectedMonths = Math.ceil(currentPeriod / 30) // Sin mínimo forzado
  const currentMonths = charts.value.demand.data?.labels?.length || 0
  
  const needsUpdate = currentMonths !== expectedMonths
  //console.log(`Demand chart check: current=${currentMonths} months, expected=${expectedMonths} months, needsUpdate=${needsUpdate}`)
  
  return needsUpdate
}

// Métodos de carga de datos
const loadData = async () => {
  try {
    loading.value = true
    //console.log('Loading analytics data...')
    
    // Cargar bodegas primero
    try {
      await store.dispatch('warehouses/fetchWarehouses')
      warehouses.value = store.getters['warehouses/allWarehouses'] || []
      //console.log('Warehouses loaded:', warehouses.value)
    } catch (error) {
      console.error('Error loading warehouses:', error)
    }
    
    const [kpisData, abcData, dinData, lowStockData] = await Promise.all([
      store.dispatch('analytics/getKPIs', filters.value),
      store.dispatch('analytics/getABCAnalysis', filters.value),
      store.dispatch('analytics/getDINAnalysis', filters.value),
      store.dispatch('analytics/getLowStockProducts', filters.value)
    ])

    kpis.value = processKPIs(kpisData)
    abcAnalysis.value = abcData.analysis || {}
    dinAnalysis.value = dinData.analysis || {}
    lowStockProducts.value = lowStockData || []
    
    // Capturar información de períodos
    if (kpisData.periods) {
      periodInfo.value = kpisData.periods
    }

    console.log('Basic data loaded, loading rotation analysis...')
    await loadRotationAnalysis()
    
    // Cargar productos para el selector
    await loadAllProducts()
    
    // Seleccionar automáticamente los top 10 productos por rotación si no hay productos seleccionados
    if (selectedProducts.value.length === 0 && rotationAnalysis.value.length > 0) {
      selectTopRotationProducts()
      // Generar predicción inicial
      await generateDemandPrediction()
    }
    
    console.log('All data loaded successfully')
  } catch (error) {
    console.error('Error loading analytics data:', error)
  } finally {
    loading.value = false
  }
}

const refreshData = async () => {
  try {
    refreshing.value = true
    await loadData()
    
    // Recrear gráficos después de cargar nuevos datos
    await nextTick()
    setTimeout(() => {
      if (safeAbcAnalysis.value && Object.keys(safeAbcAnalysis.value).length > 0) {
        createABCChart()
      }
      if (safeDinAnalysis.value && Object.keys(safeDinAnalysis.value).length > 0) {
        createDINChart()
      }
    }, 300)
    
    // Cerrar el menú de filtros después de aplicar los cambios con un pequeño delay
    setTimeout(() => {
      closeFilterMenu()
    }, 800)
  } finally {
    refreshing.value = false
  }
}

const loadRotationAnalysis = async () => {
  try {
    console.log('Loading rotation analysis...')
    const data = await store.dispatch('analytics/getRotationAnalysis', {
      ...filters.value,
      days: rotationPeriod.value
    })
    console.log('Rotation analysis loaded:', data)
    rotationAnalysis.value = data.analysis || []
    
    // Usar updateRotationChart para manejar la recreación de gráficos de forma segura
    await nextTick()
    if (rotationAnalysis.value && rotationAnalysis.value.length > 0) {
      updateRotationChart()
    }
  } catch (error) {
    console.error('Error loading rotation analysis:', error)
    rotationAnalysis.value = []
    // No mostrar error al usuario si es solo un problema de carga
  }
}

// Métodos de gráficos
const createCharts = async () => {
  // Los gráficos ABC y DIN ahora se crean automáticamente via watchers
  // Solo crear el gráfico de rotación y demanda aquí
  createRotationChart()
  createDemandChart()
}

const createABCChart = () => {
  console.log('Creating ABC chart...')
  console.log('ABC data:', safeAbcAnalysis.value)
  console.log('ABC chart ref:', abcChartRef.value)
  
  // Destruir gráfico existente de forma segura
  if (charts.value.abc) {
    try {
      charts.value.abc.destroy()
    } catch (error) {
      console.warn('Error destroying ABC chart:', error)
    }
    charts.value.abc = null
  }

  if (!abcChartRef.value || !safeAbcAnalysis.value || Object.keys(safeAbcAnalysis.value).length === 0) {
    console.log('ABC chart creation skipped - missing data or ref')
    return
  }

  try {
    const ctx = abcChartRef.value.getContext('2d')
    
    // Filtrar categorías que tienen productos
    const filteredData = Object.entries(safeAbcAnalysis.value)
      .filter(([key, category]) => category.count > 0)
    
    if (filteredData.length === 0) {
      console.log('No ABC data to display')
      return
    }
    
    const chartLabels = filteredData.map(([key, category]) => `${key} (${category.count})`)
    const chartData = filteredData.map(([key, category]) => category.value || 0)
    const chartColors = filteredData.map(([key]) => {
      switch(key) {
        case 'A': return '#4CAF50'
        case 'B': return '#FFC107' 
        case 'C': return '#F44336'
        default: return '#9E9E9E'
      }
    })
    
    console.log('ABC chart labels:', chartLabels)
    console.log('ABC chart data:', chartData)
    
    charts.value.abc = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: chartLabels,
        datasets: [{
          data: chartData,
          backgroundColor: chartColors,
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || ''
                const value = new Intl.NumberFormat('es-CO', {
                  style: 'currency',
                  currency: 'COP',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(context.parsed)
                return `${label}: ${value}`
              }
            }
          }
        }
      }
    })
    console.log('ABC chart created successfully')
  } catch (error) {
    console.error('Error creating ABC chart:', error)
    charts.value.abc = null
  }
}

const createDINChart = () => {
  console.log('Creating DIN chart...')
  console.log('DIN data:', safeDinAnalysis.value)
  console.log('DIN chart ref:', dinChartRef.value)
  
  // Destruir gráfico existente de forma segura
  if (charts.value.din) {
    try {
      charts.value.din.destroy()
    } catch (error) {
      console.warn('Error destroying DIN chart:', error)
    }
    charts.value.din = null
  }

  if (!dinChartRef.value || !safeDinAnalysis.value || Object.keys(safeDinAnalysis.value).length === 0) {
    console.log('DIN chart creation skipped - missing data or ref')
    return
  }

  try {
    const ctx = dinChartRef.value.getContext('2d')
    
    // Filtrar categorías que tienen productos
    const filteredData = Object.entries(safeDinAnalysis.value)
      .filter(([key, category]) => category.count > 0)
    
    if (filteredData.length === 0) {
      console.log('No DIN data to display')
      return
    }
    
    const chartLabels = filteredData.map(([key, category]) => `${key} (${category.count})`)
    const chartData = filteredData.map(([key, category]) => category.count || 0)
    const chartColors = filteredData.map(([key]) => {
      switch(key) {
        case 'D': return '#4CAF50'
        case 'I': return '#FFC107' 
        case 'N': return '#F44336'
        default: return '#9E9E9E'
      }
    })
    
    console.log('DIN chart labels:', chartLabels)
    console.log('DIN chart data:', chartData)
    
    charts.value.din = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: chartLabels,
        datasets: [{
          data: chartData,
          backgroundColor: chartColors,
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || ''
                return `${label} productos`
              }
            }
          }
        }
      }
    })
    console.log('DIN chart created successfully')
  } catch (error) {
    console.error('Error creating DIN chart:', error)
    charts.value.din = null
  }
}

const createRotationChart = () => {
  console.log('Creating rotation chart...')
  console.log('Filtered rotation data:', filteredRotationData.value)
  console.log('Rotation chart ref:', rotationChartRef.value)
  
  // Destruir gráfico existente de forma más segura
  safeDestroyChart('rotation')

  // Verificar que el canvas existe y está en el DOM
  if (!isCanvasValid(rotationChartRef.value)) {
    console.log('Rotation chart canvas is not valid or not in DOM')
    return
  }
  
  if (!filteredRotationData.value || filteredRotationData.value.length === 0) {
    console.log('No filtered rotation data available')
    return
  }

  try {
    const ctx = rotationChartRef.value.getContext('2d')
    
    // Verificar que el contexto es válido
    if (!ctx) {
      console.log('Could not get 2D context from canvas')
      return
    }
    
    const chartData = filteredRotationData.value.map(item => ({
      sku: item.sku || item.name,
      name: item.name || item.sku,
      rotation: item.rotation_index || 0,
      movements: item.total_movements || 0,
      stock: item.average_stock || 0
    }))
    
    console.log('Rotation chart data:', chartData)
    
    charts.value.rotation = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: chartData.map(item => item.name),
        datasets: [{
          label: 'Índice de Rotación',
          data: chartData.map(item => item.rotation),
          backgroundColor: '#3F51B5',
          borderColor: '#303F9F',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0, // Deshabilitar animaciones completamente
          onComplete: function() {
            console.log('Rotation chart animation completed')
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                const item = chartData[context[0].dataIndex]
                return `${item.name} (${item.sku})`
              },
              label: function(context) {
                const item = chartData[context.dataIndex]
                return [
                  `Índice de Rotación: ${item.rotation.toFixed(2)}`,
                  `Movimientos: ${item.movements}`,
                  `Stock Promedio: ${item.stock.toFixed(1)}`
                ]
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Índice de Rotación'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Productos'
            }
          }
        }
      }
    })
    console.log('Rotation chart created successfully')
  } catch (error) {
    console.error('Error creating rotation chart:', error)
    charts.value.rotation = null
  }
}

const createDemandChart = () => {
  console.log('Creating demand chart...')
  console.log('Demand chart ref:', demandChartRef.value)
  console.log('Demand prediction data:', demandPredictionData.value)
  
  // Destruir gráfico existente de forma más segura
  safeDestroyChart('demand')

  // Verificar que el canvas existe y está en el DOM
  if (!isCanvasValid(demandChartRef.value)) {
    console.log('Demand chart canvas is not valid or not in DOM')
    return
  }

  try {
    const ctx = demandChartRef.value.getContext('2d')
    
    // Verificar que el contexto es válido
    if (!ctx) {
      console.log('Could not get 2D context from demand canvas')
      return
    }
    
    // Usar datos de predicción futura si están disponibles
    let labels = []
    let futureData = []
    let datasets = []
    
    if (demandPredictionData.value && demandPredictionData.value.length > 0) {
      labels = demandPredictionData.value.map(item => item.month)
      futureData = demandPredictionData.value.map(item => item.totalDemand)
      
      // Crear dataset para demanda total
      datasets.push({
        label: 'Demanda Proyectada Total',
        data: futureData,
        borderColor: '#3F51B5',
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        tension: 0.1,
        fill: false
      })
      
      // Agregar datasets individuales para productos seleccionados (máximo 5)
      if (selectedProducts.value.length > 0) {
        const colors = ['#F44336', '#4CAF50', '#FF9800', '#9C27B0', '#00BCD4']
        
        selectedProducts.value.slice(0, 5).forEach((product, index) => {
          const productData = demandPredictionData.value.map(month => {
            const productPrediction = month.products.find(p => p.productId === product.id)
            return productPrediction ? productPrediction.demand : 0
          })
          
          datasets.push({
            label: product.name,
            data: productData,
            borderColor: colors[index],
            backgroundColor: colors[index] + '20',
            borderDash: [3, 3],
            tension: 0.1,
            fill: false
          })
        })
      }
    } else {
      // Datos de fallback si no hay predicciones
      labels = ['Próximo mes', 'En 2 meses', 'En 3 meses']
      futureData = [0, 0, 0]
      
      datasets.push({
        label: 'Sin datos de predicción',
        data: futureData,
        borderColor: '#9E9E9E',
        backgroundColor: 'rgba(158, 158, 158, 0.1)',
        tension: 0.1,
        fill: false
      })
    }
    
    console.log('Demand chart labels:', labels)
    console.log('Demand chart datasets:', datasets)
    
    charts.value.demand = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0, // Deshabilitar animaciones completamente
          onComplete: function() {
            console.log('Demand chart animation completed')
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                return `${context.dataset.label}: ${context.parsed.y} unidades`
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Unidades'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Meses'
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
    })
    console.log('Demand chart created successfully')
  } catch (error) {
    console.error('Error creating demand chart:', error)
    charts.value.demand = null
  }
}

const updateRotationChart = () => {
  console.log('Updating rotation chart...')
  console.log('Current rotation data:', rotationAnalysis.value)
  
  // Solo destruir el gráfico de rotación, no el de demanda
  safeDestroyChart('rotation')
  
  // Limpieza completa de Chart.js
  cleanupChartJS()
  
  // Forzar la limpieza del DOM y esperar más tiempo
  setTimeout(() => {
    // Forzar un reflow del DOM
    if (rotationChartRef.value) {
      rotationChartRef.value.offsetHeight
    }
    
    if (rotationAnalysis.value && rotationAnalysis.value.length > 0) {
      console.log('Recreating rotation chart with new data')
      
      // Verificar que el canvas sigue siendo válido antes de crear
      if (isCanvasValid(rotationChartRef.value)) {
        createRotationChart()
        
        // Solo recrear el gráfico de demanda si no existe o si cambió el período significativamente
        if (shouldUpdateDemandChart()) {
          console.log('Demand chart needs update due to period change')
          setTimeout(() => {
            if (isCanvasValid(demandChartRef.value)) {
              createDemandChart()
            }
          }, 200)
        } else {
          console.log('Demand chart is up to date, skipping recreation')
        }
      } else {
        console.log('Rotation canvas not valid, skipping chart creation')
      }
    }
  }, 300)
}

// Métodos de acciones
const exportToExcel = async () => {
  try {
    exporting.value = true
    
    // Preparar los datos para exportar
    const workbook = XLSX.utils.book_new()
    
    // Hoja de KPIs
    const kpisSheet = XLSX.utils.json_to_sheet(kpis.value || [])
    XLSX.utils.book_append_sheet(workbook, kpisSheet, 'KPIs')
    
    // Hoja de Análisis ABC
    const abcSheet = XLSX.utils.json_to_sheet(
      Object.entries(abcAnalysis.value || {}).map(([key, value]) => ({
        Categoría: key,
        Productos: value?.count || 0,
        Porcentaje: `${(value?.percentage || 0).toFixed(1)}%`,
        Valor: value?.value || 0
      }))
    )
    XLSX.utils.book_append_sheet(workbook, abcSheet, 'Análisis ABC')
    
    // Hoja de Productos con Bajo Stock
    const lowStockSheet = XLSX.utils.json_to_sheet(lowStockProducts.value || [])
    XLSX.utils.book_append_sheet(workbook, lowStockSheet, 'Bajo Stock')
    
    // Generar el archivo
    const fileName = `analytics_${format(new Date(), 'yyyy-MM-dd')}.xlsx`
    XLSX.writeFile(workbook, fileName)
  } catch (error) {
    console.error('Error exporting data:', error)
    store.dispatch('showError', 'Error al exportar los datos')
  } finally {
    exporting.value = false
  }
}

const recalculatePredictions = async () => {
  try {
    calculatingPredictions.value = true
    
    // En lugar de llamar a un endpoint, simplemente recrear el gráfico de demanda
    // con nuevos datos aleatorios basados en los datos actuales
    await nextTick()
    createDemandChart()
    
    // Simular un pequeño delay para mostrar el loading
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    console.log('Predictions recalculated successfully')
  } catch (error) {
    console.error('Error recalculating predictions:', error)
  } finally {
    calculatingPredictions.value = false
  }
}

const createPurchaseOrder = (product) => {
  if (!product) {
    console.error('No product provided to createPurchaseOrder')
    return
  }
  
  purchaseOrder.value = {
    product: product,
    quantity: product.deficit || 1,
    supplier: null,
    notes: `Orden automática generada para cubrir déficit de ${product.deficit || 1} unidades`
  }
  purchaseDialog.value = true
}

const submitPurchaseOrder = async () => {
  if (!purchaseForm.value.validate()) return
  
  try {
    submittingPurchase.value = true
    await store.dispatch('purchases/createPurchaseOrder', purchaseOrder.value)
    store.dispatch('showSuccess', 'Orden de compra creada correctamente')
    purchaseDialog.value = false
    await refreshData()
  } catch (error) {
    console.error('Error creating purchase order:', error)
    store.dispatch('showError', 'Error al crear la orden de compra')
  } finally {
    submittingPurchase.value = false
  }
}

// Función para procesar KPIs
const processKPIs = (data) => {
  if (!data) return []
  
  // Función helper para determinar la tendencia basada en el cambio
  const getTrend = (change) => {
    if (change > 0) return 'up'
    if (change < 0) return 'down'
    return 'neutral'
  }
  
  // Función helper para formatear el cambio
  const formatChange = (change) => {
    return Math.abs(change).toFixed(1)
  }
  
  const changes = data.changes || {}
  
  return [
    {
      name: 'Total Productos',
      value: data.inventory_kpis?.total_products || 0,
      type: 'number',
      trend: getTrend(changes.total_products || 0),
      change: formatChange(changes.total_products || 0)
    },
    {
      name: 'Productos Activos',
      value: data.inventory_kpis?.active_products || 0,
      type: 'number',
      trend: getTrend(changes.active_products || 0),
      change: formatChange(changes.active_products || 0)
    },
    {
      name: 'Stock Total',
      value: data.inventory_kpis?.total_stock || 0,
      type: 'number',
      trend: getTrend(changes.total_stock || 0),
      change: formatChange(changes.total_stock || 0)
    },
    {
      name: 'Valor Inventario',
      value: data.inventory_kpis?.total_value || 0,
      type: 'currency',
      trend: getTrend(changes.total_value || 0),
      change: formatChange(changes.total_value || 0)
    },
    {
      name: 'Productos Bajo Stock',
      value: data.inventory_kpis?.low_stock_products || 0,
      type: 'number',
      trend: getTrend(changes.low_stock_products || 0),
      change: formatChange(changes.low_stock_products || 0)
    },
    {
      name: 'Movimientos Mensuales',
      value: data.operational_kpis?.monthly_movements || 0,
      type: 'number',
      trend: getTrend(changes.monthly_movements || 0),
      change: formatChange(changes.monthly_movements || 0)
    },
    {
      name: 'Rotación Inventario',
      value: (data.operational_kpis?.inventory_turnover || 0) * 100,
      type: 'percentage',
      trend: getTrend(changes.inventory_turnover || 0),
      change: formatChange(changes.inventory_turnover || 0)
    }
  ]
}

// Nuevas funciones para selección de productos
const loadAllProducts = async () => {
  try {
    await store.dispatch('products/fetchProducts')
    const products = store.getters['products/allProducts']
    // Combinar con datos de rotación si están disponibles
    allProducts.value = products.map(product => {
      const rotationData = rotationAnalysis.value.find(r => r.id === product.id)
      return {
        ...product,
        rotation_index: rotationData?.rotation_index || 0
      }
    })
  } catch (error) {
    console.error('Error loading products:', error)
    allProducts.value = []
  }
}

const isProductSelected = (productId) => {
  return selectedProducts.value.some(p => p.id === productId)
}

const toggleProduct = (product) => {
  const index = selectedProducts.value.findIndex(p => p.id === product.id)
  if (index >= 0) {
    selectedProducts.value.splice(index, 1)
  } else {
    selectedProducts.value.push(product)
  }
}

const removeProduct = (productId) => {
  const index = selectedProducts.value.findIndex(p => p.id === productId)
  if (index >= 0) {
    selectedProducts.value.splice(index, 1)
    updateRotationChart()
    generateDemandPrediction()
  }
}

const selectTopRotationProducts = () => {
  if (!rotationAnalysis.value) return
  
  const topProducts = rotationAnalysis.value
    .sort((a, b) => (b.rotation_index || 0) - (a.rotation_index || 0))
    .slice(0, 10)
    .map(item => {
      const product = allProducts.value.find(p => p.id === item.id)
      return product ? { ...product, rotation_index: item.rotation_index } : null
    })
    .filter(Boolean)
  
  selectedProducts.value = topProducts
}

const clearSelectedProducts = () => {
  selectedProducts.value = []
}

const applyProductSelection = () => {
  showProductSelector.value = false
  updateRotationChart()
  generateDemandPrediction()
}

const getRotationColor = (rotationIndex) => {
  if (!rotationIndex || rotationIndex === 0) return 'grey'
  if (rotationIndex >= 2) return 'success'
  if (rotationIndex >= 1) return 'warning'
  return 'error'
}

// Función para generar predicción de demanda futura
const generateDemandPrediction = async () => {
  if (selectedProducts.value.length === 0) {
    demandPredictionData.value = []
    return
  }
  
  calculatingPredictions.value = true
  
  try {
    // Obtener datos históricos de los productos seleccionados
    const historicalData = await Promise.all(
      selectedProducts.value.map(async (product) => {
        try {
          const rotationData = rotationAnalysis.value.find(r => r.id === product.id)
          return {
            product,
            rotation_index: rotationData?.rotation_index || 0,
            total_movements: rotationData?.total_movements || 0,
            average_stock: rotationData?.average_stock || 0
          }
        } catch (error) {
          console.error(`Error getting data for product ${product.id}:`, error)
          return null
        }
      })
    )
    
    const validData = historicalData.filter(Boolean)
    
    if (validData.length === 0) {
      demandPredictionData.value = []
      return
    }
    
    // Generar predicción basada en rotación histórica
    const predictions = []
    const currentDate = new Date()
    
    for (let month = 1; month <= predictionMonths.value; month++) {
      const futureDate = new Date(currentDate)
      futureDate.setMonth(currentDate.getMonth() + month)
      
      const monthPredictions = validData.map(data => {
        // Calcular demanda proyectada basada en rotación histórica
        const baseMovements = data.total_movements || 0
        const rotationFactor = Math.max(data.rotation_index || 0, 0.1)
        
        // Aplicar tendencia estacional (simplificada)
        const seasonalFactor = 1 + (Math.sin((month - 1) * Math.PI / 6) * 0.1)
        
        // Proyección con variabilidad
        const projectedDemand = Math.round(
          (baseMovements / 30) * 30 * rotationFactor * seasonalFactor
        )
        
        return {
          product: data.product.name,
          productId: data.product.id,
          demand: Math.max(projectedDemand, 0)
        }
      })
      
      predictions.push({
        month: format(futureDate, 'MMM yyyy', { locale: es }),
        date: futureDate,
        products: monthPredictions,
        totalDemand: monthPredictions.reduce((sum, p) => sum + p.demand, 0)
      })
    }
    
    demandPredictionData.value = predictions
    
    // Actualizar gráfico de demanda
    nextTick(() => {
      createDemandChart()
    })
    
  } catch (error) {
    console.error('Error generating demand prediction:', error)
    demandPredictionData.value = []
  } finally {
    calculatingPredictions.value = false
  }
}

// Watchers para crear gráficos cuando las referencias y datos estén disponibles
watch([abcChartRef, safeAbcAnalysis], ([chartRef, data]) => {
  if (chartRef && data && Object.keys(data).length > 0 && !charts.value.abc) {
    console.log('ABC watcher triggered - creating chart')
    nextTick(() => {
      createABCChart()
    })
  }
}, { immediate: false })

watch([dinChartRef, safeDinAnalysis], ([chartRef, data]) => {
  if (chartRef && data && Object.keys(data).length > 0 && !charts.value.din) {
    console.log('DIN watcher triggered - creating chart')
    nextTick(() => {
      createDINChart()
    })
  }
}, { immediate: false })

// Watcher adicional para intentar crear gráficos después de que los datos cambien
watch([abcAnalysis, dinAnalysis], () => {
  console.log('Data changed, attempting to create charts after delay...')
  setTimeout(() => {
    if (abcChartRef.value && safeAbcAnalysis.value && Object.keys(safeAbcAnalysis.value).length > 0 && !charts.value.abc) {
      console.log('Delayed ABC chart creation attempt')
      createABCChart()
    }
    if (dinChartRef.value && safeDinAnalysis.value && Object.keys(safeDinAnalysis.value).length > 0 && !charts.value.din) {
      console.log('Delayed DIN chart creation attempt')
      createDINChart()
    }
  }, 500)
}, { immediate: false })

// Watcher para recrear gráficos cuando cambien los filtros
watch(filters, (newFilters, oldFilters) => {
  console.log('Filters changed:', { old: oldFilters, new: newFilters })
  
  // Solo recrear gráficos si realmente cambió algo relevante
  if (oldFilters && (
    newFilters.warehouse !== oldFilters.warehouse ||
    newFilters.timeRange !== oldFilters.timeRange
  )) {
    console.log('Relevant filter change detected, will recreate charts after data reload')
  }
}, { deep: true })

// Watcher específico para el período de rotación
watch(rotationPeriod, (newPeriod, oldPeriod) => {
  if (oldPeriod && newPeriod !== oldPeriod) {
    console.log(`Rotation period changed from ${oldPeriod} to ${newPeriod}`)
    // Agregar un pequeño delay para evitar llamadas múltiples rápidas
    setTimeout(() => {
      loadRotationAnalysis()
    }, 50)
  }
})

// Ciclo de vida
onMounted(async () => {
  console.log('Analytics component mounted')
  
  // Configurar Chart.js globalmente para deshabilitar animaciones
  if (window.Chart) {
    window.Chart.defaults.animation = false
    window.Chart.defaults.animations = {
      colors: false,
      x: false,
      y: false
    }
    window.Chart.defaults.transitions = {
      active: {
        animation: {
          duration: 0
        }
      }
    }
  }
  
  await loadData()
  
  // Crear gráficos ABC y DIN después de un delay si no se han creado ya
  setTimeout(() => {
    console.log('Post-mount chart creation check')
    if (abcChartRef.value && safeAbcAnalysis.value && Object.keys(safeAbcAnalysis.value).length > 0 && !charts.value.abc) {
      console.log('Creating ABC chart post-mount')
      createABCChart()
    }
    if (dinChartRef.value && safeDinAnalysis.value && Object.keys(safeDinAnalysis.value).length > 0 && !charts.value.din) {
      console.log('Creating DIN chart post-mount')
      createDINChart()
    }
  }, 500)
})

onUnmounted(() => {
  console.log('Analytics component unmounting, cleaning up charts...')
  
  // Destruir todos los gráficos de forma segura
  safeDestroyChart('abc')
  safeDestroyChart('din')
  safeDestroyChart('rotation')
  safeDestroyChart('demand')
  
  // Limpiar referencias
  charts.value = {
    abc: null,
    din: null,
    rotation: null,
    demand: null,
    demandUpdating: false
  }
})
</script>

<style scoped>
.analytics-dashboard {
  padding: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0 auto;
}

/* Override Vuetify container limitations */
.analytics-dashboard .v-row {
  max-width: none !important;
  width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.analytics-dashboard .v-col {
  max-width: none !important;
}

/* Header responsive */
.header-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 768px) {
  .header-section {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.header-title {
  flex: 1;
}

.header-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: center;
}

@media (min-width: 768px) {
  .header-actions {
    flex-direction: row;
    gap: 16px;
    width: auto;
    align-items: flex-start;
  }
}

.period-info {
  line-height: 1.4;
}

/* Low stock header */
.low-stock-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (min-width: 768px) {
  .low-stock-header {
    flex-direction: row;
    align-items: center;
    gap: 16px;
  }
}

.search-field {
  width: 100%;
  max-width: 300px;
}

/* Rotation analysis header */
.rotation-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (min-width: 768px) {
  .rotation-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }
}

.rotation-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

@media (min-width: 768px) {
  .rotation-controls {
    flex-direction: row;
    gap: 16px;
    width: auto;
  }
}

.period-select {
  width: 100%;
  max-width: 200px;
}

/* Prediction header */
.prediction-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (min-width: 768px) {
  .prediction-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
}

.prediction-title {
  flex: 1;
}

.prediction-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

@media (min-width: 768px) {
  .prediction-controls {
    flex-direction: row;
    gap: 16px;
    width: auto;
    align-items: center;
  }
}

.months-select {
  width: 100%;
  max-width: 180px;
}

.kpi-card {
  transition: transform 0.3s;
}

.kpi-card:hover {
  transform: translateY(-5px);
}

.trend {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.trend.up {
  color: #4CAF50;
}

.trend.down {
  color: #F44336;
}

.v-data-table {
  background: transparent !important;
}

.chart-container {
  position: relative;
  height: 200px;
  width: 100%;
  margin-bottom: 16px;
}

.chart-container canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.chart-container:has(canvas[height="300"]) {
  height: 300px;
}

.no-data-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

/* Estilos para el menú de filtros */
.v-menu > .v-overlay__content {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-card-title {
  font-weight: 500;
}

.v-select {
  margin-bottom: 8px;
}

/* Action button responsive */
.action-btn {
  min-width: 40px;
}

/* Mobile optimizations */
@media (max-width: 480px) {
  .analytics-dashboard {
    width: 100%;
    max-width: 100%;
    padding: 6px !important;
    margin: 0 !important;
  }
  
  .header-actions .v-btn {
    width: 100%;
    justify-content: center;
  }
  
  .rotation-controls .v-btn {
    width: 100%;
    justify-content: center;
  }
  
  .prediction-controls .v-btn {
    width: 100%;
    justify-content: center;
  }
  
  .chart-container {
    height: 250px;
  }
  
  .kpi-card {
    margin-bottom: 6px;
  }
  
  /* Force full width usage on very small screens */
  .v-row {
    margin: 0 !important;
    width: 100% !important;
    justify-content: center;
  }
  
  .v-col {
    padding: 3px !important;
  }
  
  /* Center content better on small screens */
  .header-section {
    text-align: center;
  }
  
  .header-actions {
    align-items: center;
  }
}

/* Ensure full width usage */
@media (max-width: 768px) {
  .analytics-dashboard {
    width: 100%;
    max-width: 100%;
    padding: 8px !important;
    margin: 0 !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }
  
  .v-container,
  .v-row,
  .v-col {
    padding-left: 4px !important;
    padding-right: 4px !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  .v-card {
    margin-bottom: 8px;
    width: 100%;
  }
  
  /* Remove default Vuetify margins */
  .v-row {
    margin: 0 !important;
    width: 100%;
  }
  
  .v-col {
    padding: 4px !important;
  }
  
  /* Center header content */
  .header-section {
    width: 100%;
    align-items: center;
  }
  
  /* Center all content sections */
  .v-row {
    justify-content: center;
  }
  
  /* Ensure cards use full available width */
  .v-card {
    width: 100%;
    box-sizing: border-box;
  }
}
</style> 